import axios from 'axios'

export const api = axios.create({
  baseURL: 'https://aidecision-api.jerryxu518111.workers.dev/api',
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Can add authentication tokens here
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    console.error('API Error:', error)
    return Promise.reject(error)
  }
)

// Chat function
export const sendChatMessage = async (message: string, context: any) => {
  try {
    const response = await api.post('/ai/chat', {
      message,
      context
    })
    return response.data
  } catch (error) {
    console.error('Chat API Error:', error)
    throw error
  }
}

// Decision analysis function
export const analyzeDecision = async (data: {
  decisionTitle: string
  decisionDescription: string
  options: Array<{ id: string; text: string; pros: string[]; cons: string[]; score: number }>
  criteria: Array<{ id: string; name: string; weight: number; description: string }>
}) => {
  try {
    const response = await api.post('/ai/analyze-decision', data)
    return response.data
  } catch (error) {
    console.error('Decision Analysis API Error:', error)
    throw error
  }
}

// Step-by-step analysis function
export const analyzeDecisionStepByStep = async (
  data: {
    decisionTitle: string
    decisionDescription: string
    options: Array<{ id: string; text: string; pros: string[]; cons: string[]; score: number }>
    criteria: Array<{ id: string; name: string; weight: number; description: string }>
  },
  onStepUpdate: (stepId: string, status: 'active' | 'completed' | 'error') => void,
  onStepComplete: (stepId: string, result: any) => void
) => {
  try {
    // Simulate step-by-step analysis with delays
    const steps = [
      { id: 'step1', delay: 1000, title: 'Data Collection' },
      { id: 'step2', delay: 1200, title: 'Criteria Definition' },
      { id: 'step3', delay: 1500, title: 'Option Generation' },
      { id: 'step4', delay: 2000, title: 'Option Evaluation' },
      { id: 'step5', delay: 1800, title: 'Risk Assessment' },
      { id: 'step6', delay: 1500, title: 'Conclusion' }
    ]

    // Start the actual analysis in the background
    const analysisPromise = api.post('/ai/analyze-decision', data)

    // Simulate step progress
    for (let i = 0; i < steps.length; i++) {
      const step = steps[i]
      onStepUpdate(step.id, 'active')
      
      // Wait for the simulated step duration
      await new Promise(resolve => setTimeout(resolve, step.delay))
      
      // Only mark as completed if not the last step
      if (i < steps.length - 1) {
        onStepUpdate(step.id, 'completed')
        onStepComplete(step.id, { step: step.id, completed: true })
      }
    }

    // Wait for the actual analysis to complete
    const result = await analysisPromise
    
    // Mark the last step as completed
    onStepUpdate(steps[steps.length - 1].id, 'completed')
    onStepComplete(steps[steps.length - 1].id, { step: steps[steps.length - 1].id, completed: true })
    
    return result.data
  } catch (error) {
    console.error('Step-by-Step Analysis API Error:', error)
    throw error
  }
}

// New analysis type API functions

// Causal chain analysis
export const analyzeCausalChain = async (data: {
  decisionTitle: string
  decisionDescription: string
  options: Array<{ id: string; text: string }>
}) => {
  try {
    console.log('=== analyzeCausalChain API Call ===')
    console.log('Request data:', data)
    
    const response = await api.post('/ai/analyze-causal-chain', data)
    
    console.log('=== API Response ===')
    console.log('Response status:', response.status)
    console.log('Response data:', response.data)
    console.log('Response data type:', typeof response.data)
    
    return response.data
  } catch (error) {
    console.error('Causal Chain Analysis API Error:', error)
    throw error
  }
}

// Context analysis
export const analyzeWithContext = async (data: {
  decisionTitle: string
  decisionDescription: string
  options: Array<{ id: string; text: string }>
  criteria: Array<{ id: string; name: string; weight: number; description: string }>
  contextProfile: any
}) => {
  try {
    const response = await api.post('/ai/analyze-with-context', data)
    return response.data
  } catch (error) {
    console.error('Context Analysis API Error:', error)
    throw error
  }
}

// Context analysis (supports step update)
export const analyzeWithContextStepByStep = async (
  data: {
    decisionTitle: string
    decisionDescription: string
    options: Array<{ id: string; text: string }>
    criteria: Array<{ id: string; name: string; weight: number; description: string }>
    contextProfile: any
  },
  onStepUpdate: (stepId: string, status: 'active' | 'completed' | 'error') => void,
  onStepComplete: (stepId: string, result: any) => void
) => {
  try {
    // Simulate step update
    const steps = [
      { id: 'step1', title: 'Context Analysis', description: 'Analyzing your personal context and background.' },
      { id: 'step2', title: 'Profile Integration', description: 'Integrating your profile preferences and priorities.' },
      { id: 'step3', title: 'Option Evaluation', description: 'Evaluating options based on your personal context.' },
      { id: 'step4', title: 'Personalized Scoring', description: 'Scoring options according to your preferences.' },
      { id: 'step5', title: 'Recommendation Generation', description: 'Generating personalized recommendations.' }
    ]

    for (let i = 0; i < steps.length; i++) {
      const step = steps[i]
      onStepUpdate(step.id, 'active')
      
      // Simulate each step processing time
      await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 2000))
      
      // Only mark as completed if not the last step
      if (i < steps.length - 1) {
        onStepUpdate(step.id, 'completed')
        onStepComplete(step.id, { step: step.id, completed: true })
      }
    }

    // Call the actual API
    const response = await api.post('/ai/analyze-with-context', data)
    
    // Mark the last step as completed
    onStepUpdate(steps[steps.length - 1].id, 'completed')
    onStepComplete(steps[steps.length - 1].id, { step: steps[steps.length - 1].id, completed: true })
    
    return response.data
  } catch (error) {
    console.error('Context Analysis Step by Step API Error:', error)
    throw error
  }
}

// Multi-option analysis
export const analyzeMultiOptions = async (data: {
  decisionTitle: string
  decisionDescription: string
  options: Array<{ id: string; text: string }>
  comparisonCriteria: Array<{ id: string; name: string; weight: number; description: string }>
}) => {
  try {
    console.log('=== Calling Multi-Option Analysis API ===')
    console.log('API request data:', data)
    
    const response = await api.post('/ai/analyze-multi-options', data)
    
    console.log('=== API Response Received ===')
    console.log('Response status:', response.status)
    console.log('Response data:', response.data)
    console.log('Response data type:', typeof response.data)
    
    // Data format validation and conversion
    let result = response.data
    
    // If the response is a string, try to parse JSON
    if (typeof result === 'string') {
      console.log('Response is a string, attempting to parse JSON')
      try {
        result = JSON.parse(result)
        console.log('Successfully parsed JSON:', result)
      } catch (parseError) {
        console.error('Failed to parse JSON:', parseError)
        console.log('Raw response string:', result)
        throw new Error('Invalid response format from API')
      }
    }
    
    // Validate data structure
    if (!result || typeof result !== 'object') {
      console.error('Invalid result structure:', result)
      throw new Error('Invalid response structure from API')
    }
    
    // Ensure correct format is returned
    if (result.analysis) {
      console.log('Using result.analysis')
      return { analysis: result.analysis }
    } else if (result.data) {
      console.log('Using result.data')
      return { analysis: result.data }
    } else {
      console.log('Using result directly')
      return { analysis: result }
    }
  } catch (error) {
    console.error('Multi-Option Analysis API Error:', error)
    throw error
  }
}

// Multi-option analysis (supports step update)
export const analyzeMultiOptionsStepByStep = async (
  data: {
    decisionTitle: string
    decisionDescription: string
    options: Array<{ id: string; text: string }>
    comparisonCriteria: Array<{ id: string; name: string; weight: number; description: string }>
  },
  onStepUpdate: (stepId: string, status: 'active' | 'completed' | 'error') => void,
  onStepComplete: (stepId: string, result: any) => void
) => {
  try {
    // Simulate step update
    const steps = [
      { id: 'step1', title: 'Option Analysis', description: 'Analyzing each option individually.' },
      { id: 'step2', title: 'Criteria Evaluation', description: 'Evaluating options against each criterion.' },
      { id: 'step3', title: 'Comparison Matrix', description: 'Building comparison matrix for all options.' },
      { id: 'step4', title: 'Scoring Calculation', description: 'Calculating weighted scores for each option.' },
      { id: 'step5', title: 'Ranking Analysis', description: 'Ranking options and generating recommendations.' }
    ]

    for (let i = 0; i < steps.length; i++) {
      const step = steps[i]
      onStepUpdate(step.id, 'active')
      
      // Simulate each step processing time
      await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 2000))
      
      // Only mark as completed if not the last step
      if (i < steps.length - 1) {
        onStepUpdate(step.id, 'completed')
        onStepComplete(step.id, { step: step.id, completed: true })
      }
    }

    // Call the actual API
    console.log('=== Calling Multi-Option Analysis API ===')
    console.log('API request data:', data)
    
    const response = await api.post('/ai/analyze-multi-options', data)
    
    console.log('=== API Response Received ===')
    console.log('Response status:', response.status)
    console.log('Response data:', response.data)
    console.log('Response data type:', typeof response.data)
    
    // Mark the last step as completed
    onStepUpdate(steps[steps.length - 1].id, 'completed')
    onStepComplete(steps[steps.length - 1].id, { step: steps[steps.length - 1].id, completed: true })
    
    // Data format validation and conversion
    let result = response.data
    
    // If the response is a string, try to parse JSON
    if (typeof result === 'string') {
      console.log('Response is a string, attempting to parse JSON')
      try {
        result = JSON.parse(result)
        console.log('Successfully parsed JSON:', result)
      } catch (parseError) {
        console.error('Failed to parse JSON:', parseError)
        console.log('Raw response string:', result)
        throw new Error('Invalid response format from API')
      }
    }
    
    // Validate data structure
    if (!result || typeof result !== 'object') {
      console.error('Invalid result structure:', result)
      throw new Error('Invalid response structure from API')
    }
    
    // Ensure correct format is returned
    if (result.analysis) {
      console.log('Using result.analysis')
      return { analysis: result.analysis }
    } else if (result.data) {
      console.log('Using result.data')
      return { analysis: result.data }
    } else {
      console.log('Using result directly')
      return { analysis: result }
    }
  } catch (error) {
    console.error('Multi-Option Analysis Step by Step API Error:', error)
    throw error
  }
}

// General analysis function, calls different APIs based on analysis type
export const analyzeDecisionByType = async (
  analysisType: string,
  data: {
    decisionTitle: string
    decisionDescription: string
    options: Array<{ id: string; text: string; pros?: string[]; cons?: string[]; score?: number }>
    criteria?: Array<{ id: string; name: string; weight: number; description: string }>
    contextProfile?: any
  }
) => {
  try {
    switch (analysisType) {
      case 'pros-cons':
        return await analyzeDecision({
          decisionTitle: data.decisionTitle,
          decisionDescription: data.decisionDescription,
          options: data.options.map(opt => ({
            id: opt.id,
            text: opt.text,
            pros: opt.pros || [],
            cons: opt.cons || [],
            score: opt.score || 0
          })),
          criteria: data.criteria || []
        })
      case 'causal-chain':
        return await analyzeCausalChain({
          decisionTitle: data.decisionTitle,
          decisionDescription: data.decisionDescription,
          options: data.options.map(opt => ({ id: opt.id, text: opt.text }))
        })
      case 'context':
        return await analyzeWithContext({
          decisionTitle: data.decisionTitle,
          decisionDescription: data.decisionDescription,
          options: data.options.map(opt => ({ id: opt.id, text: opt.text })),
          criteria: data.criteria || [],
          contextProfile: data.contextProfile
        })
      case 'multi-option':
        return await analyzeMultiOptions({
          decisionTitle: data.decisionTitle,
          decisionDescription: data.decisionDescription,
          options: data.options.map(opt => ({ id: opt.id, text: opt.text })),
          comparisonCriteria: data.criteria || []
        })
      default:
        throw new Error(`Unknown analysis type: ${analysisType}`)
    }
  } catch (error) {
    console.error('Analysis by type error:', error)
    throw error
  }
}

// Stream chat function


export const streamChat = async (message: string, context: any, onChunk: (content: string) => void, onComplete: () => void, onError: (error: string) => void) => {
  try {
    const response = await fetch('https://aidecision-api.jerryxu518111.workers.dev/api/ai/chat-stream', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        message,
        context
      })
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const reader = response.body?.getReader()
    if (!reader) {
      throw new Error('No response body')
    }

    const decoder = new TextDecoder()
    let buffer = ''

    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() || ''

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6)
          if (data === '[DONE]') {
            onComplete()
            return
          }

          try {
            const parsed = JSON.parse(data)
            if (parsed.content) {
              onChunk(parsed.content)
            }
            if (parsed.error) {
              onError(parsed.error)
              return
            }
          } catch (e) {
            // Ignore parsing errors
          }
        }
      }
    }
  } catch (error) {
    onError(error instanceof Error ? error.message : 'Stream chat failed')
  }
} 